import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, InputAdornment, Chip
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { db } from "../firebase";
import {
  collection, onSnapshot, addDoc, deleteDoc,
  doc, query, where, orderBy
} from "firebase/firestore";
import { salvaLog } from "../utils/logger";
import { useAuth } from "../context/AuthContext";

export default function ReferentiEvento({ eventoId }) {
  const { currentUser } = useAuth();
  const [referentiIngaggiati, setReferentiIngaggiati] = useState([]);
  const [referenti, setReferenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ricerca, setRicerca] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!eventoId) return;
    const q = query(collection(db, "referentiEvento"), where("eventoId", "==", eventoId));
    const unsub = onSnapshot(q, (snap) => {
      setReferentiIngaggiati(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [eventoId]);

  useEffect(() => {
    const q = query(collection(db, "referenti"), orderBy("cognome"));
    const unsub = onSnapshot(q, (snap) => {
      setReferenti(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  const idIngaggiati = referentiIngaggiati.map((r) => r.referenteId);

  const referentiFiltrati = referenti.filter((r) => {
    const s = ricerca.toLowerCase();
    return (
      r.nome?.toLowerCase().includes(s) ||
      r.cognome?.toLowerCase().includes(s) ||
      r.email?.toLowerCase().includes(s) ||
      r.azienda?.toLowerCase().includes(s)
    );
  });

  async function handleAggiungi(referente) {
    if (idIngaggiati.includes(referente.id)) return;
    setSaving(true);
    try {
      await addDoc(collection(db, "referentiEvento"), {
        eventoId,
        referenteId: referente.id,
        nome: referente.nome,
        cognome: referente.cognome,
        email: referente.email,
        azienda: referente.azienda,
      });
      salvaLog(currentUser, "Aggiunta referente", eventoId, "", [
        { campo: "Referente", da: "—", a: `${referente.nome} ${referente.cognome} (${referente.email || "—"})` }
      ]);
    } catch (error) {
      console.error("Errore aggiunta referente:", error);
    }
    setSaving(false);
  }

  async function handleRimuovi(id) {
    const r = referentiIngaggiati.find((r) => r.id === id);
    try {
      await deleteDoc(doc(db, "referentiEvento", id));
      salvaLog(currentUser, "Rimozione referente", eventoId, "", [
        { campo: "Referente", da: `${r?.nome} ${r?.cognome} (${r?.email || "—"})`, a: "—" }
      ]);
    } catch (error) {
      console.error("Errore rimozione referente:", error);
    }
  }

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold" color="primary">
          Referenti cliente
        </Typography>
        <Button variant="outlined" size="small" startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}>
          Aggiungi
        </Button>
      </Box>

      {referentiIngaggiati.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nessun referente associato a questo evento.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
                <TableCell><strong>Cognome</strong></TableCell>
                <TableCell><strong>Nome</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Azienda</strong></TableCell>
                <TableCell align="center"><strong>Rimuovi</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {referentiIngaggiati.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.cognome || "—"}</TableCell>
                  <TableCell>{r.nome || "—"}</TableCell>
                  <TableCell>{r.email || "—"}</TableCell>
                  <TableCell>{r.azienda || "—"}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" color="error" onClick={() => handleRimuovi(r.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Seleziona referenti</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Cerca per nome, cognome, email o azienda..."
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
            sx={{ mb: 2, mt: 1 }}
          />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
                  <TableCell><strong>Cognome</strong></TableCell>
                  <TableCell><strong>Nome</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell align="center"><strong>Stato</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {referentiFiltrati.map((r) => {
                  const giàAggiunto = idIngaggiati.includes(r.id);
                  return (
                    <TableRow key={r.id} hover
                      sx={{ cursor: giàAggiunto ? "default" : "pointer", opacity: giàAggiunto ? 0.5 : 1 }}
                      onClick={() => !giàAggiunto && handleAggiungi(r)}>
                      <TableCell>{r.cognome || "—"}</TableCell>
                      <TableCell>{r.nome || "—"}</TableCell>
                      <TableCell>{r.email || "—"}</TableCell>
                      <TableCell align="center">
                        {giàAggiunto ? (
                          <Chip label="Aggiunto" size="small" color="success" />
                        ) : (
                          <Chip label="Aggiungi" size="small" color="primary" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {referentiFiltrati.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      Nessun referente trovato
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}